import React, { useState, useEffect } from 'react';
import {
  collection, query, orderBy, onSnapshot,
  doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Clock, CheckCircle2, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import Modal from '../shared/Modal';

function StatusBadge({ status }) {
  if (status === 'pending')  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><Clock size={11} aria-hidden="true" /> Pending</span>;
  if (status === 'approved') return <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"><CheckCircle2 size={11} aria-hidden="true" /> Approved</span>;
  if (status === 'rejected') return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"><XCircle size={11} aria-hidden="true" /> Not approved</span>;
  return null;
}

function fmtDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ReviewModal({ request, reviewer, onClose }) {
  const [action, setAction]   = useState('approved');
  const [note, setNote]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const name = [reviewer.firstName, reviewer.lastName].filter(Boolean).join(' ') || reviewer.email;
      await updateDoc(doc(db, 'configRequests', request.id), {
        status:        action,
        reviewedBy:    reviewer.uid,
        reviewedByName: name,
        reviewedAt:    serverTimestamp(),
        reviewNote:    note.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal title={`Review request — ${request.clientName}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{request.category}</p>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{request.description}</p>
          <p className="text-xs text-gray-500">
            From {request.requestedByName} ({request.requestedByEmail}) · {fmtDate(request.createdAt)}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Decision</label>
            <div className="flex gap-4">
              {[
                ['approved', 'Approve'],
                ['rejected', 'Do not approve'],
              ].map(([val, label]) => (
                <label key={val} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    checked={action === val}
                    onChange={() => setAction(val)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Note to client <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <textarea
              className={inp}
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Explain the decision or next steps…"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className={`px-5 py-2 text-white text-sm rounded-lg disabled:opacity-50 ${
                action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {saving ? 'Saving…' : action === 'approved' ? 'Approve' : 'Reject'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default function ConfigRequestsManager() {
  const { userProfile } = useAuth();
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'configRequests'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  if (loading) return <p className="text-sm text-gray-600">Loading requests…</p>;

  const pending  = requests.filter(r => r.status === 'pending');
  const resolved = requests.filter(r => r.status !== 'pending');

  const renderList = (items) => (
    <div className="divide-y divide-gray-100">
      {items.map(r => (
        <div key={r.id} className="px-4 py-4 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-800">{r.clientName}</span>
              <StatusBadge status={r.status} />
            </div>
            <p className="text-xs font-medium text-gray-600">{r.category}</p>
            <p className="text-sm text-gray-700 line-clamp-2 whitespace-pre-wrap">{r.description}</p>
            <p className="text-xs text-gray-500">
              {r.requestedByName} · {fmtDate(r.createdAt)}
            </p>
            {r.reviewNote && (
              <p className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 mt-1 line-clamp-2">
                STX: {r.reviewNote}
              </p>
            )}
          </div>
          {r.status === 'pending' && (
            <button
              onClick={() => setReviewing(r)}
              className="shrink-0 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
            >
              Review
            </button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-5">

      {/* Pending */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Pending requests
            {pending.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                {pending.length}
              </span>
            )}
          </h2>
        </div>
        {pending.length === 0
          ? <p className="px-4 py-6 text-sm text-gray-500 text-center">No pending requests.</p>
          : renderList(pending)
        }
      </div>

      {/* Resolved */}
      {resolved.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowResolved(v => !v)}
            className="w-full px-4 py-3 border-b border-gray-100 flex items-center justify-between hover:bg-gray-50"
          >
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Resolved ({resolved.length})
            </h2>
            {showResolved
              ? <ChevronDown size={14} className="text-gray-400" aria-hidden="true" />
              : <ChevronRight size={14} className="text-gray-400" aria-hidden="true" />
            }
          </button>
          {showResolved && renderList(resolved)}
        </div>
      )}

      {reviewing && (
        <ReviewModal
          request={reviewing}
          reviewer={userProfile}
          onClose={() => setReviewing(null)}
        />
      )}
    </div>
  );
}
